import {
  Send,
  LineChart,
  BarChart3,
  GraduationCap,
  BookOpen,
  ShieldCheck,
  Users,
  Clock,
  AlertTriangle,
} from "lucide-react";
import { SectionLabel } from "./SocialProof";
import { BRAND, RISK_DISCLOSURE } from "./data";

export function Offer() {
  const cards = [
    {
      icon: BarChart3,
      title: "Analizy rynku",
      desc:
        "Omówienia wykresów, kluczowych poziomów i możliwych scenariuszy rynkowych.",
    },
    {
      icon: LineChart,
      title: "Sygnały tradingowe",
      desc:
        "Przejrzyste informacje o obserwowanych zagraniach, wraz z istotnymi poziomami i warunkami unieważnienia scenariusza.",
    },
    {
      icon: GraduationCap,
      title: "Edukacja i zarządzanie ryzykiem",
      desc:
        "Materiały pomagające lepiej rozumieć ryzyko, planować działania i unikać decyzji podejmowanych pod wpływem emocji.",
    },
  ];

  return (
    <section
      id="oferta"
      data-testid="offer-section"
      className="relative py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-14">
          <div className="lg:col-span-7">
            <SectionLabel>Co znajdziesz w społeczności</SectionLabel>
            <h2
              data-testid="offer-headline"
              className="mt-4 font-display font-bold tracking-tighter text-white text-4xl md:text-5xl lg:text-6xl leading-[0.98]"
            >
              Analiza rynku<br />
              w <span className="text-[#00FF66]">uporządkowanej formie.</span>
            </h2>
          </div>
          <p className="lg:col-span-5 text-neutral-400 text-lg leading-relaxed">
            Zamiast przypadkowych decyzji poznaj kontekst rynku, obserwuj ważne poziomy
            i korzystaj z analiz, które pomagają lepiej rozumieć możliwe scenariusze.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {cards.map((c, i) => {
            const Icon = c.icon;
            return (
              <div
                key={c.title}
                data-testid={`offer-card-${i}`}
                className="hs-card rounded-2xl p-7 md:p-8 flex flex-col min-h-[260px]"
              >
                <div className="inline-flex items-center justify-center h-11 w-11 rounded-lg bg-white/[0.04] text-[#00FF66] border border-white/[0.06]">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-6 font-display font-semibold tracking-tight text-white text-xl md:text-2xl">
                  {c.title}
                </h3>
                <p className="mt-3 text-neutral-400 leading-relaxed text-[15px] md:text-base">
                  {c.desc}
                </p>
              </div>
            );
          })}
        </div>

        {/* Risk disclosure near offer */}
        <div
          data-testid="offer-risk-disclosure"
          className="mt-10 flex items-start gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] px-5 py-4"
        >
          <AlertTriangle className="h-4 w-4 text-neutral-400 shrink-0 mt-0.5" />
          <p className="text-xs md:text-sm text-neutral-400 leading-relaxed">
            {RISK_DISCLOSURE}
          </p>
        </div>
      </div>
    </section>
  );
}

export function HowItWorks() {
  const steps = [
    {
      n: "01",
      icon: Send,
      title: "Dołącz do społeczności",
      desc:
        "Przejdź do Telegrama i poznaj dostępne materiały oraz zasady działania grupy.",
    },
    {
      n: "02",
      icon: BookOpen,
      title: "Analizuj i ucz się",
      desc:
        "Zapoznaj się z analizami, obserwuj poziomy cenowe i poznawaj możliwe scenariusze rynkowe.",
    },
    {
      n: "03",
      icon: GraduationCap,
      title: "Rozwijaj własne podejście",
      desc:
        "Wykorzystuj zdobytą wiedzę do lepszego rozumienia rynku i świadomej oceny ryzyka.",
    },
  ];

  return (
    <section
      id="proces"
      data-testid="how-it-works-section"
      className="relative py-24 md:py-32 bg-gradient-to-b from-[#050505] via-[#080808] to-[#050505]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto">
          <SectionLabel>Jak to działa</SectionLabel>
          <h2
            data-testid="hiw-headline"
            className="mt-4 font-display font-bold tracking-tighter text-white text-4xl md:text-5xl lg:text-6xl leading-[0.98]"
          >
            Trzy kroki do <span className="text-[#00FF66]">lepszego rozumienia rynku.</span>
          </h2>
          <p className="mt-5 text-neutral-400 text-lg">
            Przejrzysty proces, konkretne informacje i regularna analiza rynku.
          </p>
        </div>

        <div className="mt-16 relative">
          <div className="hidden lg:block absolute top-12 left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-[#00FF66]/30 to-transparent" />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-6">
            {steps.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.n}
                  data-testid={`step-card-${i}`}
                  className="relative text-center px-4"
                >
                  <div className="relative mx-auto h-24 w-24 rounded-full bg-[#0a0a0a] border border-white/[0.08] flex items-center justify-center">
                    <Icon className="h-8 w-8 text-[#00FF66]" />
                    <span className="absolute -top-2 -right-2 h-9 w-9 rounded-full bg-[#00FF66] text-black flex items-center justify-center font-display font-bold text-sm tracking-tight">
                      {s.n}
                    </span>
                  </div>
                  <h3 className="mt-7 font-display font-semibold tracking-tight text-white text-2xl">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-neutral-400 text-base leading-relaxed max-w-xs mx-auto">
                    {s.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-16 text-center">
          <a
            href={BRAND.telegram}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hiw-telegram-cta"
            className="inline-flex items-center justify-center gap-2 h-14 px-8 rounded-xl bg-[#00FF66] text-black font-bold text-base hover:bg-[#00CC52] transition-all hover:scale-[1.02] hs-button-glow"
          >
            <Send className="h-5 w-5" />
            Dołącz do Telegrama
          </a>
        </div>
      </div>
    </section>
  );
}

export function Trust() {
  const points = [
    {
      icon: ShieldCheck,
      title: "Przejrzysta komunikacja",
      desc:
        "Każdy sygnał publikuję wraz z kluczowymi poziomami i warunkami unieważnienia scenariusza. Nie obiecuję wyników, których nie można zweryfikować.",
    },
    {
      icon: Clock,
      title: "Doświadczenie na rynku krypto",
      desc:
        "Rynkiem kryptowalut interesuję się od wielu lat. Zdobytą wiedzę przekazuję w społeczności w uporządkowanej, edukacyjnej formie.",
    },
    {
      icon: Users,
      title: "Realna społeczność",
      desc:
        "Zamknięta grupa osób, które chcą lepiej rozumieć rynek — bez spamu i bez obietnic łatwego zarobku.",
    },
    {
      icon: BookOpen,
      title: "Nacisk na edukację i ryzyko",
      desc:
        "Materiały koncentrują się na metodzie analizy i zarządzaniu ryzykiem, a nie na obietnicy konkretnych rezultatów.",
    },
  ];

  return (
    <section data-testid="trust-section" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <SectionLabel>Dlaczego warto</SectionLabel>
            <h2
              data-testid="trust-headline"
              className="mt-4 font-display font-bold tracking-tighter text-white text-4xl md:text-5xl lg:text-6xl leading-[0.98]"
            >
              Spójne podejście.<br />
              <span className="text-[#00FF66]">Konkretny proces.</span>
            </h2>
            <p className="mt-6 text-neutral-400 text-lg leading-relaxed">
              W krypto łatwo trafić na wielkie obietnice. HypeSignals_4u to inne
              podejście — nacisk na strukturę analizy, kontekst rynkowy i jasno
              określone ryzyko, zamiast kuszenia szybkimi zyskami.
            </p>
          </div>

          <div className="lg:col-span-7 space-y-4">
            {points.map((p, i) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  data-testid={`trust-point-${i}`}
                  className="hs-card rounded-2xl p-7 flex gap-5"
                >
                  <div className="shrink-0 h-11 w-11 rounded-lg bg-[#00FF66]/12 border border-[#00FF66]/20 text-[#00FF66] flex items-center justify-center">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-display font-semibold tracking-tight text-white text-xl">
                      {p.title}
                    </h3>
                    <p className="mt-2 text-neutral-400 leading-relaxed text-[15px]">
                      {p.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
